import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetPlanSubscriptionDetailsQuery } from "@store/planApi";
import { Alert, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { isExpired, isLimitReached } from "@utils/subscription.util";
import {
  setExpired,
  setLimitReached,
  setOpen,
} from "@store/subscriptionAlertSlice";

const StyledLink = styled(Link)`
  color: #fff;
`;

const POLLING_INTERVAL = 60 * 1000; // 1 minute

const SubscriptionExpiredAlert: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open, limitReached, expired } = useSelector(
    (state: RootState) => state.subscriptionAlert,
  );
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: subscription } = useGetPlanSubscriptionDetailsQuery(
    { patientId: patientId! },
    { skip: !patientId, pollingInterval: POLLING_INTERVAL },
  );

  useEffect(() => {
    const expired = !!subscription && isExpired(subscription);
    const limitReached = !!subscription && isLimitReached(subscription);

    dispatch(setExpired(expired));
    dispatch(setLimitReached(limitReached));
    dispatch(setOpen(expired || limitReached));
  }, [subscription, dispatch]);

  if (!open) {
    return null;
  }

  return (
    <Alert
      variant="filled"
      severity="warning"
      sx={{ borderRadius: 0 }}
      onClose={() => dispatch(setOpen(false))}
    >
      <Stack
        flexDirection="row"
        gap="10px"
        alignItems="center"
        fontSize="14px"
        lineHeight="22px"
        fontWeight="400"
      >
        {expired ? (
          <>
            {t("subscription.expirationAlert.message")}
            <StyledLink to="/settings/billing">
              {t("subscription.expirationAlert.link")}
            </StyledLink>
          </>
        ) : limitReached ? (
          <>
            {t("subscription.limitReachedAlert.message")}
            <StyledLink to="/settings/billing">
              {t("subscription.limitReachedAlert.link")}
            </StyledLink>
          </>
        ) : null}
      </Stack>
    </Alert>
  );
};

export default SubscriptionExpiredAlert;
